import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  CExpandableFaqs,
  CExpandableFaqsBlock,
  CExpandableFaqsFragments,
  CExpandableFaqsEntry,
  CMastheadBlock,
  CMastheadFragments,
  CMinorImageMasthead,
  CMinorImageMastheadBlock,
  CMinorImageMastheadFragments,
  CSeomatic,
  CWysiwyg,
  CWysiwygFragments,
  SeomaticData,
  CFindADoctor,
  CCenteredCopy,
  CMasthead,
  CCustomComponentsLp,
  CCopyBlockBlock,
  CCustomComponentsBlogBlock,
  CCustomComponentsLpFragments,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { CraftGlobals } from 'tsconfig/craft-types'
import { gql } from '@urql/core'
import { getSiteInfo, serverSideValidateWww, useSiteInfo } from 'shared'
import {
  CFeaturedItems,
  CFeaturedItemsBlock,
  CFeaturedItemsFragment,
} from 'ui/components/organisms/c-featured-items'
import fragments from '../helpers/gql-fragments'
import { defaultClient } from '../graphql/clients'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!, $section: [String]!, $type: [String]!) {
        entry(uri: $uri, site: $site, section: $section, type: $type) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_landingPage_Entry {
            customComponentsLandingPage {
              ...customComponentsLandingPage
            }
            masthead {
              ...masthead
            }
            featuredItems {
              ...featuredItems
            }
          }
          ... on generalPages_legalPage_Entry {
            expandableFaqs {
              ...expandableFaqs
            }
            minorImageMasthead {
              ...minorImageMasthead
            }
            wysiwyg {
              ...wysiwyg
            }
          }
        }
        faqs: entries(section: "faq", site: $site) {
          ...expandableFaqsFaqEntry
        }
      }
      ${CMastheadFragments.field}
      ${CExpandableFaqsFragments.field}
      ${CFeaturedItemsFragment}
      ${CMinorImageMastheadFragments.field}
      ${CCustomComponentsLpFragments.field}
      ${CWysiwygFragments.field}
      ${CExpandableFaqsFragments.faqEntry}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
      section: ['generalPages'],
      type: ['landingPage', 'legalPage'],
    }
  )

  if (!data.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      faqs: data.faqs,
    },
  }
}

type Props = {
  entry: {
    typeHandle: string
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    expandableFaqs: Array<CExpandableFaqsBlock>
    minorImageMasthead: Array<CMinorImageMastheadBlock>
    featuredItems: Array<CFeaturedItemsBlock>
    customComponentsLandingPage: Array<CCustomComponentsBlogBlock>
    wysiwyg: Array<CCopyBlockBlock>
  }
  global: CraftGlobals
  faqs: Array<CExpandableFaqsEntry>
}

export default function CustomPage({ entry, global, faqs }: Props) {
  const siteInfo = useSiteInfo()

  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      {entry.typeHandle === 'landingPage' && (
        <>
          <CMasthead field={entry.masthead} />
          <CCustomComponentsLp
            field={entry.customComponentsLandingPage}
            global={global}
          />
          {siteInfo.isUK && entry.featuredItems.length && (
            <CFeaturedItems field={entry.featuredItems} />
          )}

          <CCenteredCopy field={global.centeredCopy} />
        </>
      )}
      {entry.typeHandle === 'legalPage' && (
        <>
          <CMinorImageMasthead field={entry.minorImageMasthead} />
          <CWysiwyg field={entry.wysiwyg} />
          <CExpandableFaqs faqs={entry.expandableFaqs} showSearchBar={true} />
          <CFindADoctor field={global.findADoctor} />
          <CCenteredCopy field={global.centeredCopy} />
        </>
      )}
    </DefaultLayout>
  )
}
